import { deleteLocalStorage, getToken, removeCookie, setToken } from '../utils/cookie';
import { AUTHENTICATION_TOKEN, REFRESH_TOKEN } from '../constants/common';
import { buildQueryString } from '../utils/common';
import config from '../../config.json';
import axios from 'axios';

let csrfTokenPromise = null;

const isUnsafeMethod = (method = 'get') => ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase());

const isProtectedLocalRoute = (url = '') => {
  if (!url) return false;

  return url.startsWith('/collection') || url.startsWith('/auth') || url.startsWith('/util');
};

const getCsrfToken = async () => {
  if (!csrfTokenPromise) {
    csrfTokenPromise = axios
      .get(`${config.PROXY_URL}/csrf-token`, {
        withCredentials: true,
      })
      .then((response) => response?.data?.csrfToken)
      .catch((error) => {
        csrfTokenPromise = null;
        throw error;
      });
  }

  return csrfTokenPromise;
};


/**
 * @description Client Side axios Instance
 * @returns {import("axios").AxiosInstance}
 */
export const getAxiosInstance = () => {
  const axiosInstance = axios.create({
    baseURL: config.PROXY_URL,
    maxBodyLength: 100000000,
    maxContentLength: 10000000,
    paramsSerializer: buildQueryString,
  });

  return axiosInstance;
};

const requestInterceptor = async (request) => {
  const user = JSON.parse(localStorage.getItem('persist:user'));
  const organization = user?.userOrganizationId?.slice(1, -1);
  const authToken = getToken(AUTHENTICATION_TOKEN);

  request.withCredentials = true;

  if (authToken) {
    request.headers['Authorization'] = authToken;
  }
  if (organization) {
    request.headers['X-Organization'] = organization;
  }

  if (isUnsafeMethod(request.method) && isProtectedLocalRoute(request.url)) {
    const csrfToken = await getCsrfToken();
    request.headers['X-CSRF-Token'] = csrfToken;
  }

  return request;
};

const requestErrorInterceptor = (error) => {
  throw error.response || error;
};

const responseInterceptor = (response) => response.data || response;

const responseErrorInterceptor = async (error) => {
  const originalRequest = error.config;

  // Check if the error is due to expired token and the request hasn't been retried yet

  if (
    (error?.response?.data?.error?.message === 'EXPIRED_TOKEN' ||
      error?.response?.data?.error?.message === 'Session expired or invalidated') &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;

    try {
      // Get refresh token and request new access token
      const refreshToken = getToken(REFRESH_TOKEN);
      const response = await axios.post(`${config.PROXY_URL}/onedoc_backend/v1/auth/refresh`, {
        refresh_token: refreshToken,
      });

      // Get the new access token from response

      // Save the new access token
      setToken(AUTHENTICATION_TOKEN, response?.data?.data?.access_token);

      // Update the failed request's authorization header
      originalRequest.headers['Authorization'] = response?.data?.data?.access_token;

      // Retry the original request with new token
      return clientService(originalRequest);
    } catch (refreshError) {
      // If refresh token fails, log out the user
      localStorage.removeItem('persist:user');
      deleteLocalStorage();
      removeCookie();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    }
  }

  // Handle other error cases
  if (error?.response?.status === 401) {
    localStorage.removeItem('persist:user');
    deleteLocalStorage();
    removeCookie();
    window.location.href = '/login';
  }

  if (error?.response?.status === 412) {
    deleteLocalStorage();
    removeCookie();
  }

  throw error.response || error;
};

const clientService = getAxiosInstance();
clientService.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
clientService.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

export default clientService;
